package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Check
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.pulltorefresh.PullToRefreshBox
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.tooling.preview.PreviewScreenSizes
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.domain.model.Account
import com.daytimegaming.ourdailyfinances.domain.model.RealEstateHolding
import com.daytimegaming.ourdailyfinances.getAndroidActivity
import com.daytimegaming.ourdailyfinances.presentation.components.BadgedActionButton
import com.daytimegaming.ourdailyfinances.presentation.preview.AccountsPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import com.daytimegaming.ourdailyfinances.presentation.util.formatIsoDateTime
import com.daytimegaming.ourdailyfinances.presentation.util.toCurrencySymbol
import org.koin.compose.viewmodel.koinViewModel
import kotlin.math.abs

sealed interface AccountListItem {
    val id: String

    data class Plaid(val account: Account) : AccountListItem {
        override val id: String get() = account.accountId
    }

    data class RealEstate(val holding: RealEstateHolding) : AccountListItem {
        override val id: String get() = holding.realEstateHoldingId
    }
}

@Composable
fun AccountsScreen(
    onAccountClick: (String) -> Unit,
    onRealEstateHoldingClick: (String) -> Unit,
    onPlaidTokenReady: (String) -> Unit,
    onManageAccounts: () -> Unit,
    onAddRealEstateHoldingClick: () -> Unit,
    showAddAccountBottomSheet: Boolean = false,
    onAddAccountBottomSheetDismissed: () -> Unit = {},
    viewModel: AccountsViewModel = koinViewModel()
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val isRefreshing by viewModel.isRefreshing.collectAsStateWithLifecycle()
    val activity = getAndroidActivity()
    viewModel.setAndroidActivity(activity)

    LaunchedEffect(Unit) {
        viewModel.refreshOrder()
        viewModel.plaidLinkTokenEvent.collect { token ->
            onPlaidTokenReady(token)
        }
    }

    AccountsScreenContent(
        state = state,
        isRefreshing = isRefreshing,
        onRefresh = viewModel::refresh,
        onRetry = viewModel::load,
        onAccountClick = onAccountClick,
        onRealEstateHoldingClick = onRealEstateHoldingClick,
        onManageAccounts = onManageAccounts,
        onAddAccountClick = { viewModel.requestAddAccount() },
        onAddRealEstateHoldingClick = {
            viewModel.checkCanAddRealEstate {
                onAddRealEstateHoldingClick()
            }
        },
        onUpgradeClick = { tier -> viewModel.upgradeSubscription(tier) },
        onDismissLimitDialog = { viewModel.dismissPremiumLimitDialog() },
        onDismissUpgradeBottomSheet = { viewModel.dismissPremiumUpgradeBottomSheet() },
        showAddAccountBottomSheet = showAddAccountBottomSheet,
        onAddAccountBottomSheetDismissed = onAddAccountBottomSheetDismissed
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AccountsScreenContent(
    state: AccountsScreenState,
    onAccountClick: (String) -> Unit,
    onRealEstateHoldingClick: (String) -> Unit,
    onManageAccounts: () -> Unit,
    onAddAccountClick: () -> Unit,
    onAddRealEstateHoldingClick: () -> Unit,
    modifier: Modifier = Modifier,
    isRefreshing: Boolean = false,
    onRefresh: () -> Unit = {},
    onRetry: () -> Unit = onRefresh,
    onUpgradeClick: (String) -> Unit = {},
    onDismissLimitDialog: () -> Unit = {},
    onDismissUpgradeBottomSheet: () -> Unit = {},
    showAddAccountBottomSheet: Boolean = false,
    onAddAccountBottomSheetDismissed: () -> Unit = {}
) {
    var showAddOptionsBottomSheet by remember { mutableStateOf(showAddAccountBottomSheet) }
    val sheetState = rememberModalBottomSheetState()

    LaunchedEffect(showAddAccountBottomSheet) {
        if (showAddAccountBottomSheet) {
            showAddOptionsBottomSheet = true
        }
    }

    Scaffold(
        modifier = modifier.fillMaxSize(),
        topBar = {
            val loaded = state as? AccountsScreenState.Loaded
            val isFreeTier = loaded?.userSubscription == null || !loaded.userSubscription.active || loaded.userSubscription.tier.equals("free", ignoreCase = true)

            if (!isFreeTier && loaded.accounts.isNotEmpty()) {
                TopAppBar(
                    title = {},
                    actions = {
                        val showWarningBadge = loaded.accounts.any { it.loginRequired || it.updateRequired }
                        val actionButton = @Composable {
                            BadgedActionButton(
                                onClick = onManageAccounts,
                                showWarningBadge = showWarningBadge,
                            )
                        }

                        if (showWarningBadge) {
                            val tooltipState = rememberTooltipState(isPersistent = true)
                            LaunchedEffect(showWarningBadge) {
                                tooltipState.show()
                            }

                            TooltipBox(
                                positionProvider = TooltipDefaults.rememberTooltipPositionProvider(
                                    positioning = TooltipAnchorPosition.Below
                                ),
                                tooltip = {
                                    PlainTooltip(
                                        caretShape = TooltipDefaults.caretShape(
                                            caretSize = DpSize(16.dp, 16.dp),
                                        )
                                    ) {
                                        Text(text = "One or more of your accounts require your attention...")
                                    }
                                },
                                state = tooltipState
                            ) {
                                actionButton()
                            }
                        } else {
                            actionButton()
                        }

                        IconButton(
                            onClick = { showAddOptionsBottomSheet = true }
                        ) {
                            Icon(
                                imageVector = Icons.Default.Add,
                                contentDescription = "Add Account",
                                tint = MaterialTheme.colorScheme.primary
                            )
                        }
                    }
                )
            }
        },
    ) { contentPadding ->
        PullToRefreshBox(
            isRefreshing = isRefreshing,
            onRefresh = onRefresh,
            modifier = Modifier
                .fillMaxSize()
                .padding(contentPadding)
        ) {
            when (state) {
                is AccountsScreenState.Loading -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                }

                is AccountsScreenState.Error -> {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .verticalScroll(rememberScrollState())
                            .padding(16.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            Text(
                                text = state.message,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodyMedium,
                                textAlign = TextAlign.Center
                            )
                            Button(onClick = onRetry) {
                                Text("Retry")
                            }
                        }
                    }
                }

                is AccountsScreenState.Loaded -> {
                    val accountsList = state.accounts
                    val holdingsList = state.realEstateHoldings
                    val userSubscription = state.userSubscription
                    val isFreeTier = userSubscription == null || !userSubscription.active || userSubscription.tier.equals("free", ignoreCase = true)
                    val hasAccounts = accountsList.isNotEmpty() || holdingsList.isNotEmpty()

                    if (isFreeTier) {
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .verticalScroll(rememberScrollState())
                                .padding(16.dp),
                            contentAlignment = Alignment.TopCenter,
                        ) {
                            Column(
                                modifier = Modifier.widthIn(max = 600.dp),
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.spacedBy(24.dp)
                            ) {
                                AccountsSubscriptionTiersSection(onUpgradeClick = onUpgradeClick)
                            }
                        }
                    } else if (!hasAccounts) {
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .verticalScroll(rememberScrollState())
                                .padding(16.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            AddAccountOptionsContent(
                                onAddAccountClick = onAddAccountClick,
                                onAddRealEstateHoldingClick = onAddRealEstateHoldingClick,
                                showTitle = false,
                                modifier = Modifier.widthIn(max = 600.dp)
                            )
                        }
                    } else {
                        val combinedItems = remember(accountsList, holdingsList, state.accountOrder) {
                            val list = accountsList.map { AccountListItem.Plaid(it) } +
                                       holdingsList.map { AccountListItem.RealEstate(it) }

                            if (state.accountOrder.isNotEmpty()) {
                                list.sortedWith { a1, a2 ->
                                    val idx1 = state.accountOrder.indexOf(a1.id)
                                    val idx2 = state.accountOrder.indexOf(a2.id)
                                    when {
                                        idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                                        idx1 != -1 -> -1
                                        idx2 != -1 -> 1
                                        else -> 0
                                    }
                                }
                            } else {
                                list
                            }
                        }

                        LazyColumn(
                            verticalArrangement = Arrangement.spacedBy(12.dp),
                            contentPadding = PaddingValues(16.dp),
                            modifier = Modifier.fillMaxSize()
                        ) {
                            item {
                                TotalAccountBalanceCard(
                                    totalBalance = state.accounts.sumOf {
                                        val bal = it.currentBalance ?: 0.0
                                        if (it.type.lowercase() == "credit" || it.type.lowercase() == "loan") -bal else bal
                                    } + state.realEstateHoldings.sumOf { it.netValue },
                                    lastSync = state.lastSync
                                )
                            }

                            items(combinedItems, key = { it.id }) { item ->
                                when (item) {
                                    is AccountListItem.Plaid -> {
                                        val account = item.account
                                        GlassCard(
                                            modifier = Modifier.fillMaxWidth(),
                                            onClick = { onAccountClick(account.accountId) }
                                        ) {
                                            Row(
                                                modifier = Modifier.fillMaxWidth(),
                                                horizontalArrangement = Arrangement.SpaceBetween,
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Column(modifier = Modifier.weight(1f)) {
                                                    Text(
                                                        text = account.displayName,
                                                        style = MaterialTheme.typography.headlineMedium,
                                                        color = MaterialTheme.colorScheme.onSurface
                                                    )
                                                    account.institutionName?.let { institution ->
                                                        Spacer(modifier = Modifier.height(2.dp))
                                                        Text(
                                                            text = institution,
                                                            style = MaterialTheme.typography.bodyMedium,
                                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                                        )
                                                    }
                                                    account.subtype?.let {
                                                        Spacer(modifier = Modifier.height(4.dp))
                                                        Box(
                                                            modifier = Modifier
                                                                .background(
                                                                    Color(0x1FBEC6E0),
                                                                    shape = RoundedCornerShape(999.dp)
                                                                )
                                                                .padding(horizontal = 8.dp, vertical = 2.dp)
                                                        ) {
                                                            Text(
                                                                text = it.uppercase(),
                                                                style = MaterialTheme.typography.labelSmall,
                                                                color = MaterialTheme.colorScheme.secondary
                                                            )
                                                        }
                                                    }
                                                }
                                                Spacer(modifier = Modifier.width(8.dp))
                                                account.currentBalance?.let { balance ->
                                                    Text(
                                                        text = "${account.isoCurrencyCode.toCurrencySymbol()}${balance.formatAmount()}",
                                                        style = MaterialTheme.typography.bodyMedium
                                                    )
                                                }
                                            }
                                        }
                                    }
                                    is AccountListItem.RealEstate -> {
                                        val holding = item.holding
                                        GlassCard(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .clickable { onRealEstateHoldingClick(holding.realEstateHoldingId) }
                                        ) {
                                            Row(
                                                modifier = Modifier.fillMaxWidth(),
                                                horizontalArrangement = Arrangement.SpaceBetween,
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Column(modifier = Modifier.weight(1f)) {
                                                    Text(
                                                        text = holding.nickname,
                                                        style = MaterialTheme.typography.headlineMedium,
                                                        color = MaterialTheme.colorScheme.onSurface
                                                    )
                                                    Spacer(modifier = Modifier.height(2.dp))
                                                    val addressText = buildString {
                                                        append(holding.addressLine1)
                                                        holding.addressLine2?.let {
                                                            if (it.isNotEmpty()) append(", ").append(it)
                                                        }
                                                        append(", ").append(holding.city).append(", ").append(holding.state).append(" ").append(holding.zipCode)
                                                    }
                                                    Text(
                                                        text = addressText,
                                                        style = MaterialTheme.typography.bodyMedium,
                                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                                    )
                                                    Spacer(modifier = Modifier.height(4.dp))
                                                    Box(
                                                        modifier = Modifier
                                                            .background(
                                                                Color(0x1FBEC6E0),
                                                                shape = RoundedCornerShape(999.dp)
                                                            )
                                                            .padding(horizontal = 8.dp, vertical = 2.dp)
                                                    ) {
                                                        Text(
                                                            text = "REAL ESTATE",
                                                            style = MaterialTheme.typography.labelSmall,
                                                            color = MaterialTheme.colorScheme.secondary
                                                        )
                                                    }
                                                }
                                                Spacer(modifier = Modifier.width(8.dp))
                                                Text(
                                                    text = "$${holding.netValue.formatAmount()}",
                                                    style = MaterialTheme.typography.bodyMedium
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    val loadedState = state as? AccountsScreenState.Loaded
    val hasAccounts = loadedState?.let { it.accounts.isNotEmpty() || it.realEstateHoldings.isNotEmpty() } == true

    if (showAddOptionsBottomSheet && hasAccounts) {
        ModalBottomSheet(
            onDismissRequest = {
                showAddOptionsBottomSheet = false
                onAddAccountBottomSheetDismissed()
            },
            sheetState = sheetState
        ) {
            AddAccountOptionsContent(
                onAddAccountClick = {
                    showAddOptionsBottomSheet = false
                    onAddAccountBottomSheetDismissed()
                    onAddAccountClick()
                },
                onAddRealEstateHoldingClick = {
                    showAddOptionsBottomSheet = false
                    onAddAccountBottomSheetDismissed()
                    onAddRealEstateHoldingClick()
                },
                modifier = Modifier
                    .safeContentPadding()
                    .padding(16.dp),
            )
        }
    }

    if (loadedState?.premiumLimitDialogMessage != null) {
        AlertDialog(
            onDismissRequest = onDismissLimitDialog,
            title = {
                Text(
                    text = "Limit Reached",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
            },
            text = {
                Text(
                    text = loadedState.premiumLimitDialogMessage,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            },
            confirmButton = {
                Button(
                    onClick = onDismissLimitDialog,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.primary,
                        contentColor = MaterialTheme.colorScheme.onPrimary
                    )
                ) {
                    Text(text = "OK", fontWeight = FontWeight.Bold)
                }
            }
        )
    }

    if (loadedState?.showPremiumUpgradeBottomSheet == true) {
        ModalBottomSheet(
            onDismissRequest = onDismissUpgradeBottomSheet
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(24.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Text(
                    text = "Upgrade to Premium",
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                loadedState.premiumUpgradeReason?.let { reason ->
                    Text(
                        text = reason,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }

                SubscriptionCard(
                    tier = "Premium",
                    price = "$10",
                    features = listOf(
                        "Add up to 12 Financial Institutions",
                        "Add up to 4 Real Estate Holdings",
                        "Create and Join Dashboards",
                        "Create Budgets"
                    ),
                    isHighlighted = true,
                    onUpgradeClick = {
                        onDismissUpgradeBottomSheet()
                        onUpgradeClick("premium")
                    },
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

@Composable
fun AddAccountOptionsContent(
    onAddAccountClick: () -> Unit,
    onAddRealEstateHoldingClick: () -> Unit,
    modifier: Modifier = Modifier,
    showTitle: Boolean = true
) {
    Column(
        modifier = modifier
            .fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        if (showTitle) {
            Text(
                text = "Add Account or Holding",
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier.padding(bottom = 8.dp)
            )
        }

        // Option 1: Add Financial Institution
        GlassCard(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(onClick = onAddAccountClick)
        ) {
            Column {
                Text(
                    text = "Add Financial Institution",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = "Link a specific financial institution such as Fidelity or Bank of America. These institutions include various account types, such as checking, savings, or investment accounts like 401ks or HSAs.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }

        // Option 2: Add Real Estate Holding
        GlassCard(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(onClick = onAddRealEstateHoldingClick)
        ) {
            Column {
                Text(
                    text = "Add Real Estate Holding",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = "Add various real estate holdings such as single family dwellings or condos. These holdings can include those with or without mortgages.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }

        Spacer(modifier = Modifier.height(16.dp))
    }
}
@Composable
private fun TotalAccountBalanceCard(
    totalBalance: Double,
    lastSync: String? = null,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 12.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        val isNegative = totalBalance < 0
        val absBalance = abs(totalBalance)
        val formattedBalance = absBalance.formatAmount()
        val parts = formattedBalance.split('.')
        val intPart = parts.getOrElse(0) { "0" }
        val decPart = parts.getOrNull(1) ?: "00"

        Text(
            text = "Total Account Balance",
            style = MaterialTheme.typography.labelSmall.copy(letterSpacing = 2.sp),
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            fontWeight = FontWeight.Bold
        )
        Spacer(modifier = Modifier.height(4.dp))
        Row(
            verticalAlignment = Alignment.Bottom
        ) {
            if (isNegative) {
                Text(
                    text = "-",
                    style = MaterialTheme.typography.titleLarge,
                    color = MaterialTheme.colorScheme.error,
                    modifier = Modifier.padding(bottom = 12.dp)
                )
            }
            Text(
                text = "$",
                style = MaterialTheme.typography.titleLarge,
                color = MaterialTheme.colorScheme.primary,
                modifier = Modifier.padding(bottom = 12.dp)
            )
            Text(
                text = intPart,
                style = MaterialTheme.typography.displayLarge.copy(fontSize = 48.sp),
                color = MaterialTheme.colorScheme.onSurface,
                fontWeight = FontWeight.Bold
            )
            Text(
                text = ".$decPart",
                style = MaterialTheme.typography.titleLarge,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(bottom = 8.dp)
            )
        }
        Spacer(modifier = Modifier.height(8.dp))
        // Synchronized indicator badge
        Row(
            modifier = Modifier
                .clip(RoundedCornerShape(999.dp))
                .background(MaterialTheme.colorScheme.secondary.copy(alpha = 0.1f))
                .border(
                    1.dp,
                    MaterialTheme.colorScheme.secondary.copy(alpha = 0.2f),
                    RoundedCornerShape(999.dp)
                )
                .padding(horizontal = 12.dp, vertical = 4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            val formattedSync = lastSync?.let { formatIsoDateTime(it) } ?: "Never"
            Text(
                text = "Last sync: $formattedSync",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.secondary,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
private fun AccountsSubscriptionTiersSection(
    onUpgradeClick: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Text(
            text = "Choose a subscription tier to link your financial institutions and automatically sync balances and transactions.",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )

        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            val isCompact = maxWidth < 600.dp

            if (isCompact) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    SubscriptionCard(
                        tier = "Standard",
                        price = "$5",
                        features = listOf(
                            "Add up to 3 Financial Institutions",
                            "Create and Join Dashboards",
                            "Create Budgets",
                            "View Recent Spending"
                        ),
                        isHighlighted = false,
                        onUpgradeClick = { onUpgradeClick("standard") },
                        modifier = Modifier.fillMaxWidth()
                    )
                    SubscriptionCard(
                        tier = "Premium",
                        price = "$10",
                        features = listOf(
                            "Add up to 12 Financial Institutions",
                            "Add up to 4 Real Estate Holdings",
                            "Create and Join Dashboards",
                            "Create Budgets"
                        ),
                        isHighlighted = true,
                        onUpgradeClick = { onUpgradeClick("premium") },
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            } else {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    SubscriptionCard(
                        tier = "Standard",
                        price = "$5",
                        features = listOf(
                            "Add up to 3 Financial Institutions",
                            "Create and Join Dashboards",
                            "Create Budgets",
                            "View Recent Spending"
                        ),
                        isHighlighted = false,
                        onUpgradeClick = { onUpgradeClick("standard") },
                        modifier = Modifier.weight(1f)
                    )
                    SubscriptionCard(
                        tier = "Premium",
                        price = "$10",
                        features = listOf(
                            "Add up to 12 Financial Institutions",
                            "Add up to 4 Real Estate Holdings",
                            "Create and Join Dashboards",
                            "Create Budgets"
                        ),
                        isHighlighted = true,
                        onUpgradeClick = { onUpgradeClick("premium") },
                        modifier = Modifier.weight(1f)
                    )
                }
            }
        }
    }
}

@Composable
private fun SubscriptionCard(
    tier: String,
    price: String,
    features: List<String>,
    isHighlighted: Boolean,
    onUpgradeClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val primaryColor = MaterialTheme.colorScheme.primary
    val highlightBorder = if (isHighlighted) Modifier.border(2.dp, primaryColor, RoundedCornerShape(12.dp)) else Modifier

    GlassCard(
        modifier = modifier
            .fillMaxWidth()
            .then(highlightBorder)
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            if (isHighlighted) {
                Box(
                    modifier = Modifier
                        .background(MaterialTheme.colorScheme.primaryContainer, RoundedCornerShape(50))
                        .border(1.dp, primaryColor.copy(alpha = 0.3f), RoundedCornerShape(50))
                        .padding(horizontal = 10.dp, vertical = 2.dp)
                ) {
                    Text(
                        text = "POPULAR",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onPrimaryContainer,
                        fontWeight = FontWeight.Bold
                    )
                }
            }
            Text(
                text = tier,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
            Row(verticalAlignment = Alignment.Bottom) {
                Text(
                    text = price,
                    style = MaterialTheme.typography.headlineLarge,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Text(
                    text = "/month",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(bottom = 4.dp, start = 2.dp)
                )
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                features.forEach { feature ->
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Default.Check,
                            contentDescription = null,
                            tint = primaryColor,
                            modifier = Modifier.size(16.dp)
                        )
                        Text(
                            text = feature,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))
            Button(
                onClick = onUpgradeClick,
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = primaryColor,
                    contentColor = MaterialTheme.colorScheme.onPrimary
                )
            ) {
                Text(text = "Upgrade to $tier", fontWeight = FontWeight.Bold)
            }
        }
    }
}

@PreviewLightDark
@PreviewScreenSizes
@Composable
fun AccountsScreenPreview(
    @PreviewParameter(AccountsPreviewParameterProvider::class) state: AccountsScreenState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface {
             AccountsScreenContent(
                 state = state,
                 onAccountClick = {},
                 onRealEstateHoldingClick = {},
                 onManageAccounts = {},
                 onAddAccountClick = {},
                 onAddRealEstateHoldingClick = {}
             )
        }
    }
}
